import { type Logger } from "./logger";
import { debugBus } from "./debug/bus";

/**
 * DqFetch handles request deduplication and retry logic
 * Provides a fetch-like API with optional retry and deduplication features
 */

interface PendingRequest {
  promise: Promise<Response>;
}

export interface DqFetchConfig {
  /**
   * Maximum number of retry attempts for failed requests
   * @default 0
   */
  maxRetries?: number;
  /**
   * Initial delay between retries in milliseconds
   * @default 1000
   */
  retryDelay?: number;
  /**
   * HTTP status codes that should trigger a retry
   * @default "[408, 429, 500, 502, 503, 504]"
   */
  retryOn?: number[];
  /**
   * Enable request deduplication
   * @default false
   */
  deduplicate?: boolean;
}

export class DqFetch {
  private _logger: Logger;
  private _pendingRequests: Map<string, PendingRequest>;

  constructor(logger: Logger) {
    this._logger = logger;
    this._pendingRequests = new Map();
  }

  /**
   * Fetch with optional retry logic and deduplication
   * API matches native fetch() but with additional config parameter
   *
   * @param url - Request URL
   * @param options - Standard fetch RequestInit options
   * @param config - Optional DqFetch configuration (retry, deduplication)
   * @returns Promise<Response> - Same as native fetch
   */
  public async fetch(
    url: string,
    options: RequestInit = {},
    config: DqFetchConfig = {},
  ): Promise<Response> {
    const {
      maxRetries = 0,
      retryDelay = 1000,
      retryOn = [408, 429, 500, 502, 503, 504],
      deduplicate = false,
    } = config;

    if (deduplicate) {
      const requestKey = this._getRequestKey(url, options);

      // Check if request already exists
      let pending = this._pendingRequests.get(requestKey);
      if (pending) {
        this._logger.printDebug(`Deduplicating request for: ${url}`);
        // Clone the response so each caller can read the body independently
        const originalResponse = await pending.promise;
        return originalResponse.clone();
      }

      this._logger.printDebug(`Starting new fetch for: ${url}`);
      const fetchPromise = this._executeFetchWithRetry(
        url,
        options,
        maxRetries,
        retryDelay,
        retryOn,
      );

      // Double-check to handle race condition when two requests start simultaneously
      pending = this._pendingRequests.get(requestKey);
      if (pending) {
        this._logger.printDebug(
          `Race condition detected, using existing request for: ${url}`,
        );
        const originalResponse = await pending.promise;
        return originalResponse.clone();
      }

      this._pendingRequests.set(requestKey, { promise: fetchPromise });

      try {
        const response = await fetchPromise;
        return response;
      } finally {
        this._pendingRequests.delete(requestKey);
      }
    }

    return this._executeFetchWithRetry(
      url,
      options,
      maxRetries,
      retryDelay,
      retryOn,
    );
  }

  /**
   * Generate a unique key for request deduplication
   * Uses URL + method + body to identify duplicate requests
   */
  private _getRequestKey(url: string, options: RequestInit): string {
    const method = options.method || "GET";
    let bodyKey = "";
    if (options.body) {
      bodyKey =
        typeof options.body === "string"
          ? options.body
          : JSON.stringify(options.body);
    }
    return `${method}:${url}:${bodyKey}`;
  }

  /**
   * Execute fetch with retry logic and exponential backoff
   */
  private async _executeFetchWithRetry(
    url: string,
    options: RequestInit,
    maxRetries: number,
    retryDelay: number,
    retryOn: number[],
  ): Promise<Response> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          // Exponential backoff: delay * 2^(attempt-1)
          const delay = retryDelay * Math.pow(2, attempt - 1);
          this._logger.printDebug(
            `Retry attempt ${attempt}/${maxRetries} for ${url} after ${delay}ms`,
          );
          await this._sleep(delay);
        }

        const method = options.method ?? "GET";
        debugBus.push({
          kind: "fetch",
          phase: "start",
          method,
          url,
          body: options.body,
          ts: Date.now(),
        });
        let response: Response;
        try {
          response = await fetch(url, options);
        } catch (fetchErr) {
          debugBus.push({
            kind: "fetch",
            phase: "end",
            method,
            url,
            error:
              fetchErr instanceof Error ? fetchErr.message : String(fetchErr),
            ts: Date.now(),
          });
          throw fetchErr;
        }
        debugBus.push({
          kind: "fetch",
          phase: "end",
          method,
          url,
          status: response.status,
          ts: Date.now(),
        });
        if (!response.ok && retryOn.includes(response.status)) {
          if (attempt < maxRetries) {
            this._logger.printDebug(
              `Request failed with status ${response.status}, retrying...`,
            );
            lastError = new Error(
              `HTTP error! status: ${response.status} ${response.statusText}`,
            );
            continue;
          }
        }

        return response;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt === maxRetries) {
          this._logger.printError(
            `All ${maxRetries + 1} attempts failed for ${url}`,
          );
          throw lastError;
        }

        this._logger.printDebug(
          `Attempt ${attempt + 1} failed: ${lastError.message}`,
        );
      }
    }

    throw lastError!;
  }

  /**
   * Sleep utility for retry delays
   */
  private _sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Create a new DqFetch instance with the provided logger
 * @param logger - Logger instance for debug/error output
 * @returns New DqFetch instance
 */
export function getDqFetch(logger: Logger): DqFetch {
  return new DqFetch(logger);
}
